import { useState, useEffect, useCallback, useRef } from 'react'
import { ShoppingBag, Save, ExternalLink, GripVertical, RefreshCw } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import Placements from '../placements/Placements'
import Packages from '../placements/Packages'

// Sponsor Store — Shopify config + curated offers for the PUBLIC Sponsors page (watch /advertise).
// Stored in app_settings (shopify_* keys), delivered via vader /api/app-config → watch /api/config
// → Sponsor.jsx, which renders custom cards via the headless Storefront API. The Storefront token is
// PUBLIC/client-safe. Offers are an ordered [{id, enabled}] list; only enabled ones show, in order.

const SF_API_VERSION = '2024-10'

function fmtPrice(p) {
  if (!p) return ''
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: p.currencyCode || 'USD' }).format(Number(p.amount)) }
  catch { return `${p.amount} ${p.currencyCode || ''}`.trim() }
}

// Sponsor Store is the single home for everything on the public sponsorship pages: the Shopify
// storefront + offers (this file) and the placement/pricing content (the Placements module,
// rendered as the second tab). `initialTab` lets the legacy /sponsors/placements route deep-link
// straight to it.
export default function SponsorStore({ initialTab = 'store' }) {
  const [tab, setTab] = useState(initialTab)
  const { api } = useAuth()
  const [settings, setSettings] = useState({})
  const [rows, setRows] = useState([])       // ordered [{ id, enabled }] — all known products
  const [products, setProducts] = useState({}) // id → { title, image, price }
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState('')
  const [prodStatus, setProdStatus] = useState('')
  const dragIndex = useRef(null)

  const set = (key, value) => setSettings(prev => ({ ...prev, [key]: value }))

  // Load saved settings + offers order.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await api('/settings?prefix=shopify_')
        if (cancelled) return
        const s = data.settings || {}
        setSettings(s)
        let saved = []
        try { saved = JSON.parse(s.shopify_offers || '[]'); if (!Array.isArray(saved)) saved = [] } catch { saved = [] }
        setRows(saved.filter(o => o && typeof o.id === 'string').map(o => ({ id: o.id, enabled: o.enabled !== false })))
      } catch (e) {
        if (!cancelled) setStatus(`Error loading: ${e.message}`)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [api])

  // Fetch all shop products from the Storefront API (client-side; public token). Reconcile with the
  // saved order: keep saved rows that still exist, append newly-found products as disabled.
  const loadProducts = useCallback(async (domain, token, currentRows) => {
    if (!domain || !token) { setProdStatus('Enter domain + Storefront token, then Save, to load products.'); return }
    setProdStatus('Loading products…')
    try {
      const res = await fetch(`https://${domain}/api/${SF_API_VERSION}/graphql.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
        body: JSON.stringify({ query: `{ products(first: 100) { edges { node { id title featuredImage { url } priceRange { minVariantPrice { amount currencyCode } } } } } }` }),
      })
      const j = await res.json()
      if (j.errors) { setProdStatus(`Storefront API error: ${j.errors[0]?.message || 'unknown'}`); return }
      const nodes = (j?.data?.products?.edges || []).map(e => e.node)
      const map = {}
      for (const n of nodes) map[n.id] = { title: n.title, image: n.featuredImage?.url || '', price: n.priceRange?.minVariantPrice }
      setProducts(map)
      // Reconcile order: saved rows still present, then any new products appended disabled.
      const known = new Set((currentRows || []).map(r => r.id))
      const reconciled = (currentRows || []).filter(r => map[r.id])
      for (const n of nodes) if (!known.has(n.id)) reconciled.push({ id: n.id, enabled: false })
      setRows(reconciled)
      setProdStatus(`${nodes.length} product${nodes.length === 1 ? '' : 's'} available.`)
    } catch (e) {
      setProdStatus(`Failed to load products: ${e.message}`)
    }
  }, [])

  // Auto-load products once settings arrive (if configured).
  useEffect(() => {
    if (loading) return
    if (settings.shopify_domain && settings.shopify_storefront_token) {
      loadProducts(settings.shopify_domain, settings.shopify_storefront_token, rows)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  function toggle(id) { setRows(rs => rs.map(r => (r.id === id ? { ...r, enabled: !r.enabled } : r))) }
  function onDragStart(i) { dragIndex.current = i }
  function onDragOver(e, i) {
    e.preventDefault()
    const from = dragIndex.current
    if (from === null || from === i) return
    setRows(rs => { const next = [...rs]; const [m] = next.splice(from, 1); next.splice(i, 0, m); return next })
    dragIndex.current = i
  }
  function onDragEnd() { dragIndex.current = null }

  async function save() {
    setSaving(true); setStatus('')
    try {
      const payload = {
        shopify_domain: String(settings.shopify_domain ?? '').trim(),
        shopify_storefront_token: String(settings.shopify_storefront_token ?? '').trim(),
        shopify_collection_id: String(settings.shopify_collection_id ?? '').trim(),
        shopify_store_title: String(settings.shopify_store_title ?? '').trim(),
        shopify_store_tagline: String(settings.shopify_store_tagline ?? '').trim(),
        shopify_mission_statement: String(settings.shopify_mission_statement ?? '').trim(),
        shopify_logo_url: String(settings.shopify_logo_url ?? '').trim(),
        shopify_share_title: String(settings.shopify_share_title ?? '').trim(),
        shopify_share_description: String(settings.shopify_share_description ?? '').trim(),
        shopify_offers: JSON.stringify(rows),
      }
      await api('/settings', { method: 'PUT', body: JSON.stringify({ settings: payload }) })
      setStatus('Saved. The Sponsors page reflects changes within ~5 min (/api/config cache).')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  const enabledCount = rows.filter(r => r.enabled).length
  const inputStyle = {
    width: '100%', background: 'var(--color-bg)', color: 'var(--color-text)',
    border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box',
  }
  const cardStyle = { background: 'var(--color-secondary)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 20, marginBottom: 16 }
  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }
  const hintStyle = { color: 'var(--color-text-muted)', fontSize: 11, margin: '4px 0 0', lineHeight: 1.45 }

  const CONFIG_FIELDS = [
    { key: 'shopify_domain', label: 'Shopify domain', placeholder: 'xxxxx.myshopify.com', hint: 'Use the myshopify.com domain from your Buy Button embed code (e.g. 37dfbc-a2.myshopify.com).' },
    { key: 'shopify_storefront_token', label: 'Storefront API access token (public)', placeholder: 'Storefront token — NOT the Admin API token', hint: 'Public/client-safe token from the Buy Button embed or a custom app’s Storefront API credentials.' },
    { key: 'shopify_store_title', label: 'Section title', placeholder: 'Sponsor Store', hint: 'Heading shown above the offers on the Sponsors page.' },
    { key: 'shopify_store_tagline', label: 'Tagline', placeholder: 'Unlock a World of Entertainment…', hint: 'Sub-headline under the title.' },
    { key: 'shopify_mission_statement', label: 'Mission statement', placeholder: 'Our mission is to bring **FREE Streaming Television**…', hint: 'Paragraph shown above the offers. Leave blank to use the default copy.', multiline: true, help: true },
    { key: 'shopify_logo_url', label: 'Logo URL', placeholder: 'https://…/logo.png', hint: 'Store logo shown in the page hero — and the image used on the social share card (padded onto a 1200×630 black card, since the logo is transparent).' },
    { key: 'shopify_share_title', label: 'Share title', placeholder: 'TenbandTV Community Sponsors', hint: 'Headline when tenband.tv/advertise is pasted into Facebook, LinkedIn, iMessage, Slack…' },
    { key: 'shopify_share_description', label: 'Share description', placeholder: 'Local businesses power free community television…', hint: 'Sub-text on that share card. Keep it under ~200 characters — social platforms truncate.', multiline: true },
  ]

  const tabBtn = active => ({
    background: 'transparent',
    color: active ? 'var(--color-text)' : 'var(--color-text-muted)',
    border: 'none',
    borderBottom: '2px solid ' + (active ? 'var(--color-primary, #dc2626)' : 'transparent'),
    padding: '8px 2px',
    marginRight: 20,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  })

  return (
    <div style={{ maxWidth: tab === 'store' ? 820 : 1000, padding: 24 }}>
      <h1 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 22, margin: '0 0 4px' }}>
        <ShoppingBag size={22} /> Sponsor Store
      </h1>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 13, margin: '0 0 12px', lineHeight: 1.5 }}>
        Everything on the public sponsorship pages. One tab per thing you edit: the Shopify checkout offers, the
        pricing grid above them, and the ad-placement detail page.
      </p>

      <div style={{ borderBottom: '1px solid var(--color-border)', marginBottom: 20 }}>
        <button type="button" onClick={() => setTab('store')} style={tabBtn(tab === 'store')}>
          Storefront &amp; Offers
        </button>
        <button type="button" onClick={() => setTab('packages')} style={tabBtn(tab === 'packages')}>
          Sponsorship Packages
        </button>
        <button type="button" onClick={() => setTab('placements')} style={tabBtn(tab === 'placements')}>
          Ad Placements
        </button>
      </div>

      {tab === 'packages' && <Packages />}
      {tab === 'placements' && <Placements />}

      {tab === 'store' && (
      <>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 13, margin: '0 0 20px', lineHeight: 1.5 }}>
        Embed Shopify offers on the public Sponsors page (<code>watch.tenband.tv/advertise</code>) as custom cards.
        Configure the storefront + branding, then choose which offers appear and their order. Enable at least one
        offer or the page falls back to a plain link-out.
      </p>

      {/* Storefront + branding */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 12px' }}>Storefront &amp; branding</h3>
        {loading ? <div style={{ color: 'var(--color-text-muted)' }}>Loading…</div> : CONFIG_FIELDS.map(({ key, label, placeholder, hint, multiline, help }) => (
          <div key={key} style={{ marginBottom: 14 }}>
            <label style={labelStyle}>{label}</label>
            {multiline
              ? <textarea value={settings[key] || ''} onChange={e => set(key, e.target.value)} placeholder={placeholder} rows={4} spellCheck={false} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
              : <input type="text" value={settings[key] || ''} onChange={e => set(key, e.target.value)} placeholder={placeholder} autoComplete="off" spellCheck={false} style={inputStyle} />}
            <p style={hintStyle}>{hint}</p>
            {help && (
              <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 12, color: 'var(--color-text-muted)', lineHeight: 1.7 }}>
                <strong style={{ color: 'var(--color-text)' }}>Highlight formatting</strong> — no HTML needed:<br />
                <code>**double asterisks**</code> → <span style={{ fontWeight: 700, color: '#fff' }}>bold white</span><br />
                <code>==double equals==</code> → <span style={{ color: '#f87171', fontWeight: 600 }}>red</span><br />
                Plain text stays as-is; line breaks are preserved.
              </div>
            )}
          </div>
        ))}
        {settings.shopify_logo_url ? <img src={settings.shopify_logo_url} alt="logo preview" style={{ maxHeight: 48, marginTop: 4 }} /> : null}
      </div>

      {/* Offers picker */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Offers <span style={{ color: 'var(--color-text-muted)', fontWeight: 400, fontSize: 12 }}>({enabledCount} shown)</span></h3>
          <button
            onClick={() => loadProducts(settings.shopify_domain, settings.shopify_storefront_token, rows)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '6px 10px', fontSize: 12, cursor: 'pointer' }}
          >
            <RefreshCw size={13} /> Reload products
          </button>
        </div>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 12, margin: '0 0 12px' }}>
          Toggle which offers show and drag to set their order. Only products available to your Storefront/Buy Button
          channel appear here. {prodStatus && <span style={{ color: 'var(--color-text)' }}>— {prodStatus}</span>}
        </p>
        {rows.length === 0 ? (
          <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>No products loaded yet.</div>
        ) : rows.map((r, i) => {
          const p = products[r.id] || { title: r.id, image: '', price: null }
          return (
            <div key={r.id} draggable onDragStart={() => onDragStart(i)} onDragOver={e => onDragOver(e, i)} onDragEnd={onDragEnd}
              title="Drag to reorder"
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 8, marginBottom: 6, border: '1px solid var(--color-border)', borderRadius: 8, background: 'var(--color-bg)', cursor: 'grab', opacity: r.enabled ? 1 : 0.55 }}>
              <span style={{ color: 'var(--color-text-muted)', display: 'flex' }}><GripVertical size={16} /></span>
              {p.image ? <img src={p.image} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} /> : <div style={{ width: 44, height: 44, borderRadius: 6, background: 'var(--color-secondary)', flexShrink: 0 }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.title}</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{fmtPrice(p.price)}</div>
              </div>
              <button type="button" onClick={() => toggle(r.id)}
                style={{ minWidth: 84, background: r.enabled ? 'var(--color-primary, #dc2626)' : 'transparent', color: r.enabled ? '#fff' : 'var(--color-text-muted)', border: '1px solid ' + (r.enabled ? 'transparent' : 'var(--color-border)'), borderRadius: 6, padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {r.enabled ? 'Shown' : 'Hidden'}
              </button>
            </div>
          )
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={save} disabled={saving || loading}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--color-primary, #dc2626)', color: '#fff', border: 'none', borderRadius: 6, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: saving || loading ? 'default' : 'pointer', opacity: saving || loading ? 0.6 : 1 }}>
          <Save size={15} /> {saving ? 'Saving…' : 'Save'}
        </button>
        <a href="https://watch.tenband.tv/advertise" target="_blank" rel="noopener noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-text-muted)' }}>
          <ExternalLink size={13} /> Preview the Sponsors page
        </a>
      </div>
      {status && <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: '10px 0 0' }}>{status}</p>}
      </>
      )}
    </div>
  )
}
